import React, { Dispatch, SetStateAction, useEffect, useState, useRef, useCallback } from 'react';
import './WorkflowEditor.scss';
import Node from './Node';
import Edge from './Edge';
import { NodeType, EdgeType, BlockType } from './models';
import AppointmentNodeDetails from './node-details/AppointmentNodeDetails';
import ComplaintNodeDetails from './node-details/ComplaintNodeDetails';
import TransferNodeDetails from './node-details/TransferNodeDetails';
import DefaultNodeDetails from './node-details/DefaultNodeDetails';

export interface MenuStateType {
  visible: boolean;
  x: number;
  y: number;
  parentId: string | null;
}

interface WorkflowEditorProps {
  isNewWorkflow: boolean;
  setDefinition: (definition: any) => void;
  definition?: string;
}

export const BLOCK_WIDTH = 200;
export const BLOCK_HEIGHT = 70;
const HORIZONTAL_SPACING = 80;
const VERTICAL_SPACING = 20;
const BLOCK_TYPES = [
  { type: BlockType.APPOINTMENT, label: 'Appointment' },
  { type: BlockType.COMPLAINT, label: 'Complaint' },
  { type: BlockType.TRANSFER, label: 'Transfer to Human Agent' },
  { type: BlockType.SEND_MESSAGE, label: 'Send a Message' },
];

const initialNodes: NodeType[] = [
  {
    id: 'start',
    type: BlockType.START,
    position: { x: 100, y: 90 },
    children: [],
    parentId: null,
  },
  {
    id: 'appointment',
    type: BlockType.APPOINTMENT,
    position: { x: 400, y: 90 },
    parentId: 'start',
  },{
    id: 'transfer',
    type: BlockType.TRANSFER,
    position: { x: 400, y: 180 },
    parentId: 'start',
  },
];

const initialEdges: EdgeType[] = [
  { id: 'e_start_appointment', source: 'start', target: 'appointment' },
  { id: 'e_start_support', source: 'start', target: 'support' },
  { id: 'e_start_transfer', source: 'start', target: 'transfer' },
];

const layoutNodes = (nodes: NodeType[], setNodes: Dispatch<SetStateAction<NodeType[]>>) => {
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, { ...n }]));

  function layoutSubtree(nodeId: string, x: number, y: number): number {
    const node = nodeMap[nodeId];
    node.position = { x, y };
    const children = nodes.filter(n => n.parentId === nodeId);
    let nextY = y;
    for (const child of children) {
      nextY = layoutSubtree(child.id, x + BLOCK_WIDTH + HORIZONTAL_SPACING, nextY);
    }
    return children.length
      ? nextY + VERTICAL_SPACING
      : y + BLOCK_HEIGHT + VERTICAL_SPACING;
  }

  layoutSubtree('start', HORIZONTAL_SPACING, VERTICAL_SPACING);
  setNodes(Object.values(nodeMap));
};

const useNodeDragAndDrop = (nodes: NodeType[], setNodes: Dispatch<SetStateAction<NodeType[]>>, offset: {x: number, y: number}, zoom: number)  => {
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
  const dragStart = useRef<{x: number, y: number} | null>(null);
  const wasDragged = useRef(false);

  const handleNodeMouseDown = useCallback((e: React.MouseEvent, nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      setDraggingNodeId(nodeId);
      setDragOffset({
        x: e.clientX - node.position.x * zoom - offset.x,
        y: e.clientY - node.position.y * zoom - offset.y,
      });
      dragStart.current = { x: e.clientX, y: e.clientY };
      wasDragged.current = false;
    }
  }, [nodes, zoom, offset.x, offset.y]);

  useEffect(() => {
    function handleNodeMouseMove(e: MouseEvent) {
      if (draggingNodeId && dragOffset) {
        const dx = e.clientX - (dragStart.current?.x || 0);
        const dy = e.clientY - (dragStart.current?.y || 0);
        if (Math.sqrt(dx * dx + dy * dy) > 2) {
          wasDragged.current = true;
        }
        setNodes(nodes => nodes.map(node =>
          node.id === draggingNodeId
            ? {
                ...node,
                position: {
                  x: (e.clientX - dragOffset.x - offset.x) / zoom,
                  y: (e.clientY - dragOffset.y - offset.y) / zoom,
                },
              }
            : node
        ));
      }
    }

    function handleNodeMouseUp(e: MouseEvent) {
      setDraggingNodeId(null);
      setDragOffset(null);
      dragStart.current = null;
    }

    if (draggingNodeId) {
      window.addEventListener('mousemove', handleNodeMouseMove);
      window.addEventListener('mouseup', handleNodeMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleNodeMouseMove);
      window.removeEventListener('mouseup', handleNodeMouseUp);
    };
  }, [draggingNodeId, dragOffset, offset.x, offset.y, zoom, setNodes]);

  return { draggingNodeId, handleNodeMouseDown, wasDragged, dragStart };
};

const useContextMenu = (setMenuState: Dispatch<SetStateAction<MenuStateType>>) => {
  useEffect(() => {
    const closeMenu = () => setMenuState(s => s.visible ? { ...s, visible: false, parentId: null } : s);
    window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, [setMenuState]);
};

const WorkflowEditor: React.FC<WorkflowEditorProps> = ({ isNewWorkflow, definition, setDefinition }) => {
  const [nodes, setNodes] = useState<NodeType[]>(isNewWorkflow ? initialNodes : []);
  const [edges, setEdges] = useState<EdgeType[]>(isNewWorkflow ? initialEdges : []);
  const [menuState, setMenuState] = useState<MenuStateType>({visible: false, x: 0, y: 0, parentId: null});
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({x: 0, y: 0});
  const [dragging, setDragging] = useState(false);
  const offsetStart = useRef<{x: number, y: number}>({x: 0, y: 0});
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const { draggingNodeId, handleNodeMouseDown, wasDragged, dragStart } = useNodeDragAndDrop(nodes, setNodes, offset, zoom);

  useEffect(() => {
    if (definition) {
      const workflowDefinition = definition as { nodes?: NodeType[], edges?: EdgeType[] };
      if (workflowDefinition.nodes) setNodes(workflowDefinition.nodes);
      if (workflowDefinition.edges) setEdges(workflowDefinition.edges);
    } else if (isNewWorkflow) {
      setNodes(initialNodes);
      setEdges(initialEdges);
    } else {
      setNodes([]);
      setEdges([]);
    }
  }, []);

  useEffect(() => {
    setDefinition({ nodes, edges });
  }, [nodes, edges]);

  useContextMenu(setMenuState);

  const handleNodeClick = (nodeId: string) => {
    if (wasDragged.current) return;
    if (nodes.find(n => n.id === nodeId)?.type === BlockType.START) return;
    setSelectedNodeId(nodeId);
  };

  const handleSaveNodeContext = (nodeId: string, context: string) => {
    setNodes(nodes => nodes.map(node => node.id === nodeId ? { ...node, context } : node));
  };

  const handleCloseNodeDetails = () => setSelectedNodeId(null);

  const handleAddChildBlock = (type: BlockType) => {
    if (!menuState.parentId) return;
    const parent = nodes.find(n => n.id === menuState.parentId);
    if (!parent) return;
    const siblings = nodes.filter(n => n.parentId === parent.id);
    const lastSibling = siblings.reduce((prev, current) => (prev && prev.position.y > current.position.y) ? prev : current, null as NodeType | null);
    const startY = parent.position.y + BLOCK_HEIGHT + VERTICAL_SPACING;
    const newY = lastSibling 
      ? Math.round((lastSibling.position.y + BLOCK_HEIGHT + VERTICAL_SPACING) / VERTICAL_SPACING) * VERTICAL_SPACING
      : Math.round(startY / VERTICAL_SPACING) * VERTICAL_SPACING;
    const newX = Math.round((parent.position.x + BLOCK_WIDTH + HORIZONTAL_SPACING) / HORIZONTAL_SPACING) * HORIZONTAL_SPACING;
    const newId = `${type}_${Date.now()}`;
    const newNode: NodeType = {
      id: newId,
      type,
      position: { x: newX, y: newY },
      parentId: parent.id,
    };
    setNodes(nodes => [...nodes, newNode]);
    setEdges(edges => [...edges, { id: `e_${parent.id}_${newId}`, source: parent.id, target: newId }]);
    setMenuState({visible: false, x: 0, y: 0, parentId: null});
    layoutNodes([...nodes, newNode], setNodes);
  };

  const handleBlockRightClick = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    setMenuState({visible: true, x: e.clientX, y: e.clientY, parentId: nodeId});
  };

  const handleDeleteNode = (nodeId: string) => {
    const collectToDelete = (id: string, acc: Set<string>) => {
      acc.add(id);
      nodes.filter(n => n.parentId === id).forEach(n => collectToDelete(n.id, acc));
    };
    const toDelete = new Set<string>();
    collectToDelete(nodeId, toDelete);
    setNodes(nodes => nodes.filter(n => !toDelete.has(n.id)));
    setEdges(edges => edges.filter(e => !toDelete.has(e.source) && !toDelete.has(e.target)));
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      let newZoom = zoom - e.deltaY * 0.001;
      newZoom = Math.max(0.3, Math.min(2, newZoom));
      setZoom(newZoom);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as any).tagName === 'svg') {
      setDragging(true);
      dragStart.current = {x: e.clientX, y: e.clientY};
      offsetStart.current = {...offset};
    }
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragging && dragStart.current) {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setOffset({x: offsetStart.current.x + dx, y: offsetStart.current.y + dy});
    }
  };
  const handleMouseUp = () => setDragging(false);

  const renderNodes = () => nodes.map((node) => (
    <Node
      key={node.id}
      node={node}
      onDeleteNode={handleDeleteNode}
      onBlockRightClick={handleBlockRightClick}
      onMouseDown={handleNodeMouseDown}
      onClick={handleNodeClick}
    />
  ));

  const renderEdges = () => edges.map((edge) => (
    <Edge key={edge.id} edge={edge} nodes={nodes} />
  ));

  const renderContextMenu = () => {
    if (!menuState.visible) return null;
    const parent = nodes.find(n => n.id === menuState.parentId);
    const childrenTypes = parent ? nodes.filter(n => n.parentId === parent.id).map(n => n.type) : [];
    const availableTypes = BLOCK_TYPES.filter(bt => !childrenTypes.includes(bt.type));
    if (availableTypes.length === 0) {
      return <div className="context-menu-item" style={{ color: '#aaa', cursor: 'default', pointerEvents: 'none' }}>{'<<empty>>'}</div>;
    }
    return availableTypes.map(bt => (
      <div
        key={bt.type}
        className="context-menu-item"
        onClick={() => handleAddChildBlock(bt.type)}
      >
        {bt.label}
      </div>
    ));
  };

  const renderNodeDetails = () => {
    if (!selectedNodeId) return null;
    const selectedNode = nodes.find(n => n.id === selectedNodeId);
    if (!selectedNode) {
      setTimeout(() => setSelectedNodeId(null), 0);
      return null;
    }
    switch (selectedNode.type) {
      case BlockType.APPOINTMENT:
        return <AppointmentNodeDetails node={selectedNode} onClose={handleCloseNodeDetails} onSave={handleSaveNodeContext} />;
      case BlockType.COMPLAINT:
        return <ComplaintNodeDetails node={selectedNode} onClose={handleCloseNodeDetails} onSave={handleSaveNodeContext} />;
      case BlockType.TRANSFER:
        return <TransferNodeDetails node={selectedNode} onClose={handleCloseNodeDetails} onSave={handleSaveNodeContext} />;
      default:
        return <DefaultNodeDetails node={selectedNode} onClose={handleCloseNodeDetails} onSave={handleSaveNodeContext} />;
    }
  };

  return (
    <div className="workflow-preview">
      <svg
        width="100%"
        height="400"
        className={dragging ? 'dragging' : 'draggable'}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <g transform={`translate(${offset.x},${offset.y}) scale(${zoom})`}>
          <defs>
            <marker id="arrow" markerWidth="10" markerHeight="10" refX="10" refY="5" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L10,5 L0,10 L2,5 Z" fill="#888" />
            </marker>
          </defs>
          {renderEdges()}
          {renderNodes()}
        </g>
      </svg>
      <div className="zoom-controls">
        <button onClick={() => setZoom(prevZoom => Math.min(2, prevZoom + 0.1))}>+</button>
        <button onClick={() => setZoom(prevZoom => Math.max(0.3, prevZoom - 0.1))}>-</button>
      </div>
      {menuState.visible && (
        <div
          className="context-menu"
          style={{left: menuState.x, top: menuState.y}}
        >
          {renderContextMenu()}
        </div>
      )}
      {/* Overlay for node details */}
      {selectedNodeId && (
        <div className="node-details-overlay">
          {renderNodeDetails()}
        </div>
      )}
    </div>
  );
};

export default WorkflowEditor; 